import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/api';
import {
  OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE,
  useSelectedOperatingGroup,
} from '../hooks/useSelectedOperatingGroup';
import { RestaurantEmployment } from '../types/restaurantPersonnel';
import { KitchenEmployeeDetails, User } from '../types';
import { extractApiErrorMessage } from '../utils/apiError';
import { isRestaurantOperatingContext } from '../utils/operatingContext';

const positionRoles = [
  'Кухар',
  'Посудомийка',
  'Прибиральниця',
  'Бармен',
  'Офіціант',
  'Працівник',
  'Адміністратор',
  'Шеф кухар',
  'ШефКухар',
  'Керівник',
];

const ProfilePage: React.FC = () => {
  const { groupId: selectedGroupId, changedInAnotherTab } = useSelectedOperatingGroup();
  const [me, setMe] = useState<User | null>(null);
  const [employment, setEmployment] = useState<RestaurantEmployment | null>(null);
  const [legacyHr, setLegacyHr] = useState<KitchenEmployeeDetails | null>(null);
  const [message, setMessage] = useState('');
  const [employmentMessage, setEmploymentMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const requestGeneration = useRef(0);

  const loadProfile = useCallback(async () => {
    const generation = ++requestGeneration.current;
    setLoading(true);
    setMessage('');
    setEmploymentMessage('');
    setEmployment(null);
    setLegacyHr(null);
    const restaurantContext = isRestaurantOperatingContext(selectedGroupId);
    const legacyContext = !selectedGroupId;
    const [meResult, employmentResult] = await Promise.allSettled([
      api.get<User>('/users/me'),
      changedInAnotherTab
        ? Promise.reject(OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE)
        : restaurantContext
          ? api.get<{ employment: RestaurantEmployment }>('/restaurant-personnel/me')
          : legacyContext
            ? api.get<KitchenEmployeeDetails>('/kitchen-employees/me')
            : Promise.reject('Оберіть ресторан, щоб переглянути працевлаштування.'),
    ]);
    if (generation !== requestGeneration.current) return;
    if (meResult.status === 'fulfilled') {
      setMe(meResult.value.data);
    } else {
      setMe(null);
      setMessage(extractApiErrorMessage(meResult.reason, 'Не вдалося завантажити профіль.'));
    }

    if (employmentResult.status === 'fulfilled') {
      const employmentData = employmentResult.value.data;
      if ('employment' in employmentData) {
        setEmployment(employmentData.employment);
        setLegacyHr(null);
      } else {
        setEmployment(null);
        setLegacyHr(employmentData);
      }
    } else {
      setEmployment(null);
      setLegacyHr(null);
      setEmploymentMessage(extractApiErrorMessage(
        employmentResult.reason,
        restaurantContext
          ? 'Дані працевлаштування ресторану недоступні.'
          : 'Дані HR недоступні.',
      ));
    }
    setLoading(false);
  }, [changedInAnotherTab, selectedGroupId]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => () => { requestGeneration.current += 1; }, []);

  if (!me) {
    return (
      <div className="page-shell" style={{ color: 'var(--crm-text)' }}>
        <h2>Мій профіль</h2>
        {loading ? (
          <p role="status" style={{ color: 'var(--crm-text-muted)' }}>Завантаження...</p>
        ) : (
          <>
            <div role="alert" style={{ padding: 12, marginBottom: 14, background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)' }}>
              {message || 'Не вдалося завантажити профіль.'}
            </div>
            <button
              type="button"
              onClick={() => void loadProfile()}
              disabled={loading}
              style={{ minHeight: 44, padding: '9px 16px', background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              Спробувати ще раз
            </button>
          </>
        )}
      </div>
    );
  }

  const p = me.profile;
  const mainRole = employment?.position ||
    legacyHr?.position ||
    me.roles.find((r) => positionRoles.includes(r)) || p?.position || me.roles?.[0] || '—';
  const hireDate = employment?.hireDate || p?.hireDate;
  const name = employment?.fullName ||
    (p ? `${p.firstName} ${p.lastName}`.trim() : '');
  const phone = employment?.phone || p?.phone;

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>Мій профіль</h2>
      {message && <p style={{ color: 'var(--crm-danger)' }}>{message}</p>}
      <div className="responsive-grid-2" style={{ marginTop: '20px' }}>
        <div style={{ background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', padding: '20px', borderRadius: 'var(--crm-radius-md)', boxShadow: 'var(--crm-shadow-sm)' }}>
          <h3 style={{ color: 'var(--crm-text)', marginTop: 0 }}>Особиста інформація</h3>
          <p><strong>Логін:</strong> {me.username}</p>
          <p><strong>Email:</strong> {me.email}</p>
          {(employment || p) && (
            <>
              <p><strong>Ім'я:</strong> {name}</p>
              <p><strong>Телефон:</strong> {phone || '—'}</p>
              <p><strong>Роль (посада):</strong> {mainRole}</p>
              <p><strong>Дата прийому:</strong> {hireDate ? new Date(hireDate).toLocaleDateString('uk-UA') : '—'}</p>
            </>
          )}
          <p><strong>Ролі в системі:</strong> {me.roles.join(', ') || 'Немає'}</p>
        </div>

        <div style={{ background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', padding: '20px', borderRadius: 'var(--crm-radius-md)', boxShadow: 'var(--crm-shadow-sm)' }}>
          <h3 style={{ color: 'var(--crm-text)', marginTop: 0 }}>
            {employment ? 'Працевлаштування в обраному ресторані' : 'HR-статистика'}
          </h3>
          {employment ? (
            <>
              <p><strong>Працівник:</strong> {employment.fullName}</p>
              <p><strong>Посада:</strong> {employment.position}</p>
              <p><strong>Початок працевлаштування:</strong> {new Date(employment.effectiveFrom).toLocaleDateString('uk-UA')}</p>
              <p><strong>Табелювання:</strong> {employment.useAttendance ? 'Увімкнено' : 'Вимкнено'}</p>
            </>
          ) : legacyHr ? (
            <>
              <p><strong>Підсумковий коефіцієнт:</strong> {legacyHr.summaryCoefficient.toFixed(4)}</p>
              <p><strong>Відпрацьовано годин:</strong> {legacyHr.totalHoursWorked.toFixed(2)}</p>
              <p><strong>Зважені години:</strong> {legacyHr.totalWeightedHours.toFixed(2)}</p>
              <p><strong>Табелювання:</strong> {legacyHr.useAttendance ? 'Увімкнено' : 'Вимкнено'}</p>
            </>
          ) : (
            <p>{employmentMessage || 'Дані HR ще не сформовані.'}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
